import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { FileSpreadsheet, Plus, Sparkles, Copy, Save, Trash2, GitBranch, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";

const FIELD_TYPES = [
  { id: "boolean", label: "Yes / No (boolean)" },
  { id: "single_choice", label: "Single choice" },
  { id: "multi_choice", label: "Multi choice" },
  { id: "number", label: "Number (with limits)" },
  { id: "date", label: "Date" },
  { id: "short_text", label: "Short text" },
  { id: "long_text", label: "Long text (needs justification)" },
];

const emptyField = {
  field_id: "", label: "", type: "boolean",
  mandatory: true, allow_missing: true, options: [],
  constraints: { min: null, max: null, integer: false, decimals: 0, unit: "" },
  free_text_justification: "", endpoint_ids: [], help_text: "",
};

export default function FormBuilder() {
  const [search] = useSearchParams();
  const initStudy = search.get("study_id") || "";
  const [studies, setStudies] = useState([]);
  const [studyId, setStudyId] = useState(initStudy);
  const [forms, setForms] = useState([]);
  const [current, setCurrent] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDesc, setAiDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { (async () => setStudies((await api.get("/studies")).data))(); }, []);
  useEffect(() => { if (studyId) refresh(); }, [studyId]);

  const refresh = async () => setForms((await api.get(`/ecrf/forms?study_id=${studyId}`)).data);

  const newBlankForm = () => {
    if (!studyId) return toast.error("Pick a study first");
    setCurrent({
      study_id: studyId, name: "New Form", version: "1.0", state: "Draft",
      fields: [{ ...emptyField, field_id: "f_1" }], skip_rules: [],
    });
  };

  const openForm = async (fid) => setCurrent((await api.get(`/ecrf/forms/${fid}`)).data);

  const save = async () => {
    if (!current.name.trim()) return toast.error("Name required");
    // Assign field_ids if blank
    const clean = { ...current, fields: current.fields.map((f, i) => ({ ...f, field_id: f.field_id || `f_${i + 1}` })) };
    try {
      const { data } = await api.post("/ecrf/forms", clean);
      toast.success(data.form_id === current.form_id ? "Form updated" : "Form created", {
        description: data.unlinked_fields?.length ? `⚠ ${data.unlinked_fields.length} field(s) not linked to any endpoint (Principle 7)` : undefined,
      });
      setCurrent(data);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const cloneForm = async (fid) => {
    const { data } = await api.post(`/ecrf/forms/${fid}/clone`, { study_id: studyId });
    toast.success("Cloned — Principle 5: Be lazy 😉");
    setCurrent(data); refresh();
  };

  const runAi = async () => {
    if (!aiDesc.trim()) return toast.error("Study description required");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ecrf/ai-generate", { study_description: aiDesc, study_id: studyId }, { timeout: 60000 });
      const skeleton = {
        study_id: studyId, name: data.name || "AI-Generated Form", version: "1.0", state: "Draft",
        fields: data.fields || [], skip_rules: data.skip_rules || [],
      };
      setCurrent(skeleton);
      setAiOpen(false); setAiDesc("");
      toast.success("AI skeleton created", { description: `${skeleton.fields.length} field(s) + ${skeleton.skip_rules.length} skip rule(s). Review then Save.` });
    } catch (e) { toast.error(e.response?.data?.detail || "AI generation failed"); }
    finally { setAiLoading(false); }
  };

  const updateField = (i, patch) => {
    const arr = [...current.fields]; arr[i] = { ...arr[i], ...patch }; setCurrent({ ...current, fields: arr });
  };
  const removeField = (i) => setCurrent({ ...current, fields: current.fields.filter((_, j) => j !== i) });
  const addField = () => setCurrent({ ...current, fields: [...current.fields, { ...emptyField, field_id: `f_${current.fields.length + 1}` }] });

  const addSkipRule = () => setCurrent({ ...current, skip_rules: [...(current.skip_rules || []), { when_field: "", op: "==", value: "", action: "hide", target_fields: [] }] });
  const updateRule = (i, patch) => {
    const arr = [...current.skip_rules]; arr[i] = { ...arr[i], ...patch }; setCurrent({ ...current, skip_rules: arr });
  };
  const removeRule = (i) => setCurrent({ ...current, skip_rules: current.skip_rules.filter((_, j) => j !== i) });

  const study = studies.find((s) => s.study_id === studyId);
  const endpoints = study?.endpoints || [];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="vq-overline mb-2">Clinical / eCRF</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Form Builder</h1>
            <p className="text-slate-600 mt-2 max-w-3xl">Every field is guardrailed by the 7 principles. Free text is discouraged; numbers have hard limits; missing values require a reason; skip logic drives what the subject sees.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={studyId} onValueChange={setStudyId}>
              <SelectTrigger className="w-64 h-11" data-testid="fb-study-select"><SelectValue placeholder="Pick a study…" /></SelectTrigger>
              <SelectContent>
                {studies.map((s) => <SelectItem key={s.study_id} value={s.study_id}>{s.protocol_id} · {s.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={newBlankForm} disabled={!studyId} data-testid="fb-new-blank"
                    className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Blank
            </Button>
            <Button onClick={() => setAiOpen(true)} disabled={!studyId} data-testid="fb-ai-btn"
                    className="h-11 bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold">
              <Sparkles className="w-4 h-4 mr-2" /> AI Assist
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">Forms in study ({forms.length})</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {forms.map((f) => (
                <div key={f.form_id} className="p-3 hover:bg-slate-50 group">
                  <button onClick={() => openForm(f.form_id)} data-testid={`fb-open-${f.form_id}`} className="w-full text-left">
                    <div className="font-semibold text-sm text-slate-900">{f.name}</div>
                    <div className="text-[10px] font-mono text-slate-400">{f.form_id} · v{f.version} · {(f.fields || []).length} field(s)</div>
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => cloneForm(f.form_id)} data-testid={`fb-clone-${f.form_id}`}
                          className="mt-1 text-xs text-[#0055FF] hover:bg-blue-50 h-7 px-2">
                    <Copy className="w-3 h-3 mr-1" /> Clone
                  </Button>
                </div>
              ))}
              {forms.length === 0 && studyId && <div className="p-6 text-sm text-slate-500 text-center">No forms yet.</div>}
              {!studyId && <div className="p-6 text-sm text-slate-500 text-center">Pick a study above.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {current ? (
              <div className="vq-card p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="flex-1">
                    <Label className="text-xs font-semibold">Form name</Label>
                    <Input value={current.name} data-testid="fb-form-name"
                           onChange={(e) => setCurrent({ ...current, name: e.target.value })} className="mt-1.5 max-w-md" />
                  </div>
                  <Button onClick={save} data-testid="fb-save-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>

                <div className="vq-overline mb-3">Fields ({current.fields.length})</div>
                <div className="space-y-3 mb-6">
                  {current.fields.map((f, i) => (
                    <FieldEditor key={f.field_id || `field-${i}`} field={f} idx={i} endpoints={endpoints}
                                 update={(p) => updateField(i, p)} remove={() => removeField(i)} />
                  ))}
                </div>
                <Button variant="outline" onClick={addField} data-testid="fb-add-field-btn" className="border-slate-900 text-slate-900">
                  <Plus className="w-4 h-4 mr-2" /> Add Field
                </Button>

                <div className="vq-overline mt-6 mb-3 flex items-center gap-2"><GitBranch className="w-4 h-4" /> Skip Rules ({(current.skip_rules || []).length})</div>
                <div className="space-y-2">
                  {(current.skip_rules || []).map((r, i) => (
                    <SkipRuleEditor key={`rule-${i}`} rule={r} idx={i} fields={current.fields}
                                    update={(p) => updateRule(i, p)} remove={() => removeRule(i)} />
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addSkipRule} data-testid="fb-add-rule-btn" className="mt-2">
                  <Plus className="w-3 h-3 mr-1" /> Add Skip Rule
                </Button>
              </div>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Pick a form or create a new one.</div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0055FF]" /> AI Form Assist</DialogTitle>
            <DialogDescription>Describe the study; Claude generates a 7-principle-compliant field set + skip rules. You review before saving.</DialogDescription>
          </DialogHeader>
          <Textarea rows={7} value={aiDesc} data-testid="fb-ai-desc"
                    onChange={(e) => setAiDesc(e.target.value)}
                    placeholder="Example: 'I'm running a 12-week PMCF for a wearable insulin pump in adults with T1D. Primary endpoint is change in HbA1c from baseline. Safety endpoints: device malfunctions and hypoglycemic events. Collect at baseline, week 4, week 8, and week 12.'" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button onClick={runAi} disabled={aiLoading} data-testid="fb-ai-submit" className="bg-[#0055FF] text-white">
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Thinking…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function FieldEditor({ field, idx, endpoints, update, remove }) {
  const [expanded, setExpanded] = useState(false);
  const noEndpoint = !(field.endpoint_ids || []).length;
  return (
    <div className={`border rounded p-3 ${noEndpoint ? "border-amber-300 bg-amber-50/30" : "border-slate-200"}`}>
      <div className="grid grid-cols-12 gap-2 items-start">
        <div className="col-span-1 pt-2 text-xs font-mono text-slate-400">#{idx + 1}</div>
        <div className="col-span-5">
          <Label className="text-[10px] font-semibold">Label</Label>
          <Input value={field.label} data-testid={`fb-field-label-${idx}`}
                 onChange={(e) => update({ label: e.target.value })} className="mt-1 h-9" />
        </div>
        <div className="col-span-3">
          <Label className="text-[10px] font-semibold">Type</Label>
          <Select value={field.type} onValueChange={(v) => update({ type: v })}>
            <SelectTrigger className="mt-1 h-9" data-testid={`fb-field-type-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2 flex flex-col gap-1 pt-4">
          <label className="flex items-center gap-1 text-[10px]">
            <Checkbox checked={field.mandatory} onCheckedChange={(v) => update({ mandatory: !!v })} data-testid={`fb-field-mand-${idx}`} /> Mandatory
          </label>
          <label className="flex items-center gap-1 text-[10px]">
            <Checkbox checked={field.allow_missing} onCheckedChange={(v) => update({ allow_missing: !!v })} /> Allow N/A
          </label>
        </div>
        <div className="col-span-1 flex gap-1 pt-4">
          <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)} className="h-7 px-2 text-[10px]">{expanded ? "−" : "+"}</Button>
          <Button size="sm" variant="ghost" onClick={remove} className="h-7 px-2 text-red-600"><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
          {(field.type === "single_choice" || field.type === "multi_choice") && (
            <div><Label className="text-[10px] font-semibold">Options (comma-separated)</Label>
              <Input value={(field.options || []).join(", ")}
                     onChange={(e) => update({ options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                     className="mt-1 h-9" placeholder="Yes, No, Prefer not to say" /></div>
          )}
          {field.type === "number" && (
            <div className="grid grid-cols-5 gap-2">
              <div><Label className="text-[10px]">Min</Label><Input type="number" value={field.constraints?.min ?? ""} onChange={(e) => update({ constraints: { ...(field.constraints || {}), min: e.target.value === "" ? null : Number(e.target.value) } })} className="h-8" /></div>
              <div><Label className="text-[10px]">Max</Label><Input type="number" value={field.constraints?.max ?? ""} onChange={(e) => update({ constraints: { ...(field.constraints || {}), max: e.target.value === "" ? null : Number(e.target.value) } })} className="h-8" /></div>
              <div><Label className="text-[10px]">Unit</Label><Input value={field.constraints?.unit ?? ""} onChange={(e) => update({ constraints: { ...(field.constraints || {}), unit: e.target.value } })} className="h-8" placeholder="mmHg, kg…" /></div>
              <div><Label className="text-[10px]">Decimals</Label><Input type="number" value={field.constraints?.decimals ?? 0} onChange={(e) => update({ constraints: { ...(field.constraints || {}), decimals: Number(e.target.value) } })} className="h-8" /></div>
              <label className="flex items-center gap-1 text-[10px] pt-6"><Checkbox checked={field.constraints?.integer} onCheckedChange={(v) => update({ constraints: { ...(field.constraints || {}), integer: !!v } })} /> Integer only</label>
            </div>
          )}
          {field.type === "long_text" && (
            <div><Label className="text-[10px] font-semibold text-red-700">Free-text justification (Principle 3 — required)</Label>
              <Input value={field.free_text_justification} onChange={(e) => update({ free_text_justification: e.target.value })} className="mt-1 h-9" placeholder="Why can't this be a structured field?" /></div>
          )}
          <div><Label className="text-[10px] font-semibold">Endpoint linkage (Principle 7)</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {endpoints.map((ep) => (
                <label key={ep.endpoint_id} className="inline-flex items-center gap-1 text-[10px] border border-slate-200 rounded px-2 py-1 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={(field.endpoint_ids || []).includes(ep.endpoint_id)}
                            onCheckedChange={(v) => {
                              const cur = new Set(field.endpoint_ids || []);
                              if (v) cur.add(ep.endpoint_id); else cur.delete(ep.endpoint_id);
                              update({ endpoint_ids: [...cur] });
                            }} data-testid={`fb-field-ep-${idx}-${ep.endpoint_id}`} />
                  <span>{ep.name}</span>
                </label>
              ))}
              {endpoints.length === 0 && <span className="text-[10px] text-slate-500 italic">Define endpoints on the Study first.</span>}
            </div>
            {noEndpoint && endpoints.length > 0 && <div className="text-[10px] text-amber-700 mt-1">⚠ No endpoint — this field will be flagged on save.</div>}
          </div>
          <div><Label className="text-[10px] font-semibold">Help text (optional)</Label>
            <Input value={field.help_text ?? ""} onChange={(e) => update({ help_text: e.target.value })} className="mt-1 h-9" /></div>
        </div>
      )}
    </div>
  );
}

function SkipRuleEditor({ rule, idx, fields, update, remove }) {
  return (
    <div className="border border-slate-200 rounded p-2 bg-slate-50 grid grid-cols-12 gap-2 items-center">
      <div className="col-span-1 text-xs font-mono text-slate-400">R{idx + 1}</div>
      <div className="col-span-2 text-[10px] font-semibold">When</div>
      <Select value={rule.when_field} onValueChange={(v) => update({ when_field: v })}>
        <SelectTrigger className="col-span-3 h-8"><SelectValue placeholder="Field" /></SelectTrigger>
        <SelectContent>{fields.map((f) => <SelectItem key={f.field_id} value={f.field_id}>{f.label || f.field_id}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={rule.op} onValueChange={(v) => update({ op: v })}>
        <SelectTrigger className="col-span-2 h-8"><SelectValue /></SelectTrigger>
        <SelectContent>{["==", "!=", ">", ">=", "<", "<=", "in", "not_in", "is_missing"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
      <Input className="col-span-2 h-8" placeholder="value" value={rule.value ?? ""}
             onChange={(e) => { const v = e.target.value; update({ value: v === "true" ? true : v === "false" ? false : isNaN(Number(v)) || v === "" ? v : Number(v) }); }} />
      <Select value={rule.action} onValueChange={(v) => update({ action: v })}>
        <SelectTrigger className="col-span-1 h-8"><SelectValue /></SelectTrigger>
        <SelectContent>{["show", "hide", "require"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="ghost" onClick={remove} className="col-span-1 text-red-600"><Trash2 className="w-3 h-3" /></Button>
      <div className="col-span-12 flex items-center gap-2">
        <span className="text-[10px] text-slate-500">Targets:</span>
        {fields.filter((f) => f.field_id !== rule.when_field).map((f) => (
          <label key={f.field_id} className="text-[10px] inline-flex items-center gap-1 cursor-pointer">
            <Checkbox checked={(rule.target_fields || []).includes(f.field_id)}
                      onCheckedChange={(v) => {
                        const cur = new Set(rule.target_fields || []);
                        if (v) cur.add(f.field_id); else cur.delete(f.field_id);
                        update({ target_fields: [...cur] });
                      }} />
            <span>{f.label || f.field_id}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
